import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { FullMenu, resolvePortalPath, isExternalPortalPath } from "./TopNavigationBar";
import { useRouteStore } from "../../store/RouteConfigStore";
import { getToken, getUserNm } from "../../authentication";
import { NavLink, useLocation } from "react-router-dom";

export const Header = () => {
  const { routeLoading, portal, routes } = useRouteStore();
  const location = useLocation();
  const rootPath = useMemo(() => portal === 'PORTAL_TSP' ? '/tsp' : portal === 'PORTAL_PMS' ? '/biz' : '', [portal]);
  /** 기업지원(USP)은 signout이 /signin/signout, TSP는 /tsp/signout */
  const signOutPath = useMemo(() => (portal === 'PORTAL_TSP' ? '/tsp/signout' : '/signin/signout'), [portal]);
  // PORTAL_PMS에서도 로그인 라우트는 /signin (prefix 없음)으로 고정되어 있음.
  // rootPath가 /biz로 설정되면 /biz/signin으로 잘못 이동함.
  const signInPath = '/signin'

  // ===== 상태 관리 =====
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [isMenuActive, setIsMenuActive] = useState(false);
  const [isFullMenuActive, setIsFullMenuActive] = useState(false);
  const [isMoMenuActive, setIsMoMenuActive] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 1200 : false);
  
  const lastScrollTop = useRef(0);
  const fullmenuAreaRef = useRef<HTMLDivElement>(null);
  const hamBtnPcRef = useRef<HTMLButtonElement>(null);

  // ===== vh CSS 변수 설정 =====
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  // ===== 화면 크기 감지 =====
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1200;
      setIsMobile(mobile);
      
      // PC로 전환 시: 모바일 메뉴가 열려있었으면 PC 전체메뉴로 전환
      if (!mobile) {
        setIsMoMenuActive(prev => {
          if (prev) {
            setIsFullMenuActive(true);
          }
          return false;
        });
        setIsHeaderHidden(false);
      }
      // 모바일로 전환 시: PC 전체메뉴가 열려있었으면 모바일 메뉴로 전환
      if (mobile) {
        setIsFullMenuActive(prev => {
          if (prev) {
            setIsMoMenuActive(true);
          }
          return false;
        });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ===== 스크롤 시 헤더 숨김 =====
  useEffect(() => {
    const handleScroll = () => {
      if (isFullMenuActive || isMoMenuActive) return;
      const scrollTop = window.scrollY;
      
      if (isMobile) {
        if (scrollTop > 118 && scrollTop > lastScrollTop.current) {
          setIsHeaderHidden(true);
        } else {
          setIsHeaderHidden(false);
        }
      } else {
        setIsHeaderHidden(scrollTop > 118);
      }
      lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, isFullMenuActive, isMoMenuActive]);

  // ===== body overflow 제어 =====
  useEffect(() => {
    const html = document.documentElement;
    if (isFullMenuActive || isMoMenuActive) {
      html.classList.add('overflow-hidden');
    } else {
      html.classList.remove('overflow-hidden');
    }
    return () => html.classList.remove('overflow-hidden');
  }, [isFullMenuActive, isMoMenuActive]);

  const handlePcFullMenuToggle = useCallback(() => {
    setIsFullMenuActive(prev => !prev);
  }, []);

  const handlePcFullMenuClose = useCallback(() => {
    setIsFullMenuActive(false);
    hamBtnPcRef.current?.focus();
  }, []);

  const handleMoMenuToggle = useCallback(() => {
    setIsMoMenuActive(prev => !prev);
  }, []);

  // 모바일 메뉴 닫기 (dim 클릭)
  const handleDimClick = useCallback(() => {
    setIsMoMenuActive(false);
    setIsFullMenuActive(false);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (isFullMenuActive) handlePcFullMenuClose();
      if (isMoMenuActive) setIsMoMenuActive(false);
    }
  }, [isFullMenuActive, isMoMenuActive, handlePcFullMenuClose]);

  // ===== 포커스 트랩 =====
  useEffect(() => {
    if (!isFullMenuActive || !fullmenuAreaRef.current) return;
    const container = fullmenuAreaRef.current;
    const focusableSelector = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(container.querySelectorAll(focusableSelector))
        .filter((el: any) => el.offsetParent !== null) as HTMLElement[];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    const firstFocusable = container.querySelector(focusableSelector) as HTMLElement;
    firstFocusable?.focus();
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isFullMenuActive]);

  // 필터링된 routes 메모이제이션
  const filteredRoutes = useMemo(() => 
    routes.filter(r => r.readYn),
    [routes]
  );

  // 경로 매칭 헬퍼: 경로 구분자(/)를 고려한 정확한 매칭
  const isPathMatch = useCallback((currentPath: string, targetPath: string) => {
    if (!targetPath) return false;
    if (currentPath === targetPath) return true;
    // targetPath가 이미 /로 끝나면 그대로, 아니면 / 추가하여 경계 체크
    const prefix = targetPath.endsWith('/') ? targetPath : targetPath + '/';
    return currentPath.startsWith(prefix);
  }, []);

  // 현재 경로와 가장 길게 매칭되는 메뉴의 인덱스를 찾는 함수
  // 1차 메뉴 자체 path + 2차/3차 자식 path 모두 검사
  const activeRouteIndex = useMemo(() => {
    const currentPath = location.pathname;
    let bestIndex = -1;
    let longestMatchLength = 0;
    
    filteredRoutes.forEach((route: any, i: number) => {
      const menuPath = resolvePortalPath(route.path, rootPath);
      
      // 1차 메뉴 자체 경로 매칭 체크
      if (menuPath && isPathMatch(currentPath, menuPath)) {
        if (menuPath.length > longestMatchLength) {
          bestIndex = i;
          longestMatchLength = menuPath.length;
        }
      }
      
      // 2차, 3차 자식 메뉴 경로 매칭 체크 (부모 path와 무관하게 독립적으로 검사)
      if (route.children) {
        route.children.forEach((child: any) => {
          const childPath = resolvePortalPath(child.path, rootPath);
          if (childPath && isPathMatch(currentPath, childPath)) {
            if (childPath.length > longestMatchLength) {
              bestIndex = i;
              longestMatchLength = childPath.length;
            }
          }
          
          if (child.children) {
            child.children.forEach((depth3: any) => {
              const depth3Path = resolvePortalPath(depth3.path, rootPath);
              if (depth3Path && isPathMatch(currentPath, depth3Path)) {
                if (depth3Path.length > longestMatchLength) {
                  bestIndex = i;
                  longestMatchLength = depth3Path.length;
                }
              }
            });
          }
        });
      }
    });
    
    return bestIndex;
  }, [location.pathname, rootPath, filteredRoutes, isPathMatch]);

  const closeAllMenus = useCallback(() => {
    setIsFullMenuActive(false);
    setIsMoMenuActive(false);
  }, []);

  const headerClassName = [
    'header',
    // 스크롤로 인한 hidden (메뉴 안 열렸을 때) 또는 모바일 메뉴 열렸을 때
    (isHeaderHidden && !isMoMenuActive && !isFullMenuActive) || isMoMenuActive ? 'hidden' : '',
    isMenuActive && !isFullMenuActive ? 'menu-active' : '',
    isFullMenuActive ? 'fullmenu-active' : '',
    isMoMenuActive ? 'mo-menu-active' : '',
  ].filter(Boolean).join(' ');

  const fullmenuAreaClassName = [
    'fullmenu-area',
    isFullMenuActive ? 'active' : '',
    isMoMenuActive ? 'mo-active' : '',
  ].filter(Boolean).join(' ');

  const logoTitle = portal === 'PORTAL_PMS' ? '사업관리시스템 홈으로 이동' : 'AI 기업지원 홈으로 이동';
  const logoClass = portal === 'PORTAL_PMS' ? 'biz' : '';
  const homePath = portal === 'PORTAL_PMS' ? '/biz' : portal === 'PORTAL_TSP' ? '/tsp' : '/';

  return (
    <>
      <div className='header-dim' onClick={handleDimClick}></div>
      
      <header className={headerClassName}>
        <div className="header-head">
          <div className='container'>
            <div className="header-inner">
              <h1 className={`logo ${logoClass}`}>
                <NavLink to={homePath} title={logoTitle}></NavLink>
              </h1>
              <div className="header-head-right">
                {getToken() ? (
                  <>
                    <span className="header-txt-btn"><b>{getUserNm()}</b>님</span>
                    <NavLink to={signOutPath} className="header-txt-btn">로그아웃</NavLink>
                  </>
                ) : (
                  <>
                    <a
                      href={`${signInPath}?nextUrl=${window.btoa(window.location.href)}`}
                      className="header-txt-btn login"
                    >
                      로그인
                    </a>
                    <NavLink to={'/signup'} onClick={(e) => {
                      if(portal === 'PORTAL_TSP') {
                        e.preventDefault();
                        window.location.href = `${process.env.REACT_APP_DOMAIN}/signup`;
                      }
                    }} className="header-txt-btn join">회원가입</NavLink>
                  </>
                )}
                <button type="button" className="search-btn" title="통합검색" onClick={() => window.open(`${process.env.REACT_APP_DOMAIN}/search`, '_blank')}>
                  <i className="icon icon-search"></i>
                </button>
                <button type="button" className={`ham-btn ham-btn-mo ${isMoMenuActive ? 'active' : ''}`} title={isMoMenuActive ? '전체메뉴 닫기' : '전체메뉴 열기'} onClick={handleMoMenuToggle}>
                  <span className="ham-btn-line"></span>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {!routeLoading && (
          <div className="header-body">
            <div className='container'>
              <div className="header-inner">
                <nav className="gnb-w" onMouseEnter={() => !isFullMenuActive && setIsMenuActive(true)} onMouseLeave={() => setIsMenuActive(false)}>
                  <ul className="gnb">
                    {filteredRoutes.map((route, i) => {
                      const routePath = resolvePortalPath(route.path, rootPath);
                      const routeExternal = isExternalPortalPath(routePath, rootPath);
                      return (
                        <li key={i} className="gnb-li">
                          {routeExternal ? (
                            <a href={routePath || '#'} className={i === activeRouteIndex ? "gnb-link active" : "gnb-link"}>
                              <span>{route.label}</span>
                            </a>
                          ) : (
                            <NavLink to={routePath || '#'} className={() => i === activeRouteIndex ? "gnb-link active" : "gnb-link"}>
                              <span>{route.label}</span>
                            </NavLink>
                          )}
                          <div className="lnb">
                            <ul className="lnb-w">
                              {route.children?.filter(f => f.readYn && f.label !== '사업신청').map((child, j) => {
                                const childPath = resolvePortalPath(child.path, rootPath);
                                const childExternal = isExternalPortalPath(childPath, rootPath);
                                const isChildActive = childPath ? (location.pathname === childPath || location.pathname.startsWith(childPath + '/')) : false;
                                return (
                                  <li key={j} className="lnb-li">
                                    {childExternal ? (
                                      <a href={childPath} className={isChildActive ? "lnb-link active" : "lnb-link"}>
                                        <span>{child.label}</span>
                                      </a>
                                    ) : (
                                      <NavLink to={childPath} className={() => isChildActive ? "lnb-link active" : "lnb-link"}>
                                        <span>{child.label}</span>
                                      </NavLink>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
                <div className='header-body-right'>
                  <button type='button' title='통합검색' className='search-btn' onClick={() => window.open(`${process.env.REACT_APP_DOMAIN}/search`, '_blank')}>
                    <i className='icon icon-search'></i>
                  </button>
                  <button ref={hamBtnPcRef} title={isFullMenuActive ? '전체메뉴 닫기' : '전체메뉴 열기'} type='button' className={`ham-btn ham-btn-pc ${isFullMenuActive ? 'active' : ''}`} onClick={handlePcFullMenuToggle}>
                    <span className='ham-btn-line'></span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="header-bg" onMouseEnter={() => !isFullMenuActive && setIsMenuActive(true)} onMouseLeave={() => setIsMenuActive(false)}></div>
      </header>

      <div ref={fullmenuAreaRef} className={fullmenuAreaClassName} tabIndex={-1} aria-modal='true' role='dialog' aria-labelledby='fullmenu-title' onKeyDown={handleKeyDown}>
        <div className='container'>
          <div className='fullmenu-w'>
            <div className='mo-menu'>
              {!getToken() ? (
                <>
                  <div className='mo-menu-top'><strong>로그인을 해 주세요.</strong></div>
                  <div className='mo-menu-btn-w'>
                    <a
                      href={`${signInPath}?nextUrl=${window.btoa(window.location.href)}`}
                      className='mo-menu-btn primary'
                      onClick={closeAllMenus}
                    >
                      로그인
                    </a>
                    <NavLink to={'/signup'} onClick={(e) => { closeAllMenus(); if(portal === 'PORTAL_TSP') { e.preventDefault(); window.location.href = `${process.env.REACT_APP_DOMAIN}/signup`; }}} className='mo-menu-btn'>회원가입</NavLink>
                  </div>
                </>
              ) : (
                <>
                  <div className='mo-menu-top'>
                    <strong>{getUserNm()}님, 안녕하세요.</strong>
                    <a href={signOutPath} className='mo-logout' onClick={closeAllMenus}>로그아웃</a>
                  </div>
                  <div className='mo-menu-btn-w'>
                    <a href={`${rootPath}/mypage`} className='mo-menu-btn secondary' onClick={closeAllMenus}>마이페이지</a>
                  </div>
                </>
              )}
            </div>
            <h2 id='fullmenu-title'>전체메뉴</h2>
            <FullMenu closeMenu={closeAllMenus} isMobile={isMobile} />
            <button type='button' title='전체메뉴 닫기' className='fullmenu-close' onClick={handlePcFullMenuClose}>
              <i className='icon icon-close'></i>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
